import type { TypedDocumentNode } from '@graphql-typed-document-node/core';

import {
  type CompositionV2Entity,
  type GetPayV2Query,
  PAY_V2_QUERY,
  PAY_V2_TOPGEOS_QUERY,
  type PayV2Entity,
  type PayV2Req,
} from '@revelio/data-access';

import { useIsTaxonomyResolved } from '../../../filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import { useGeographyMode } from '../../../filters/hooks/useGeographyMode';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import { getCompensationV2Filters } from '../../../filters/utils/getCompensationFilters';
import { positionsEntityLookup } from '../../../filters/utils/getV2Filters';
import { useChart } from '../../Chart/ChartProvider';
import { useTabContentConfig } from '../../PageConfigProvider/PageConfigProvider';
import type { CompensationChartType } from '../../PageConfigProvider/types/chart';
import type {
  CompensationSecondaryDimension,
  SecondaryDimensionValues,
} from '../../PageConfigProvider/types/dimension';
import type { PayMetricValues } from '../../PageConfigProvider/types/metric';
import { isOverviewPage } from '../../PageConfigProvider/utils/isOverviewPage';
import { usePageState } from '../../PageState/PageStateProvider';
import {
  type BoxSeriesDataSet,
  type BreakdownBoxDataSet,
  type BreakdownSingleDataSet,
  type DataProviderChildren,
  DataProviderQueryStatus,
  type DataProviderSet,
  type DistributionDataSet,
  type SingleDataSet,
  type TimeseriesDataSet,
} from '../types';
import { convertPayV2Data } from '../utils/convert-compensation-data';
import { useUrqlQueryWithStatus } from '../utils/data-provider-results-utils';
import { CompensationTopCompetitorsDataProvider } from './CompensationTopCompetitorsDataProvider';
import { CompensationTopNDataProvider } from './CompensationTopNDataProvider';

export type CompensationData =
  | SingleDataSet
  | TimeseriesDataSet
  | BoxSeriesDataSet
  | BreakdownBoxDataSet
  | BreakdownSingleDataSet
  | DistributionDataSet;

type CompensationDataProviderProps = {
  metric: PayMetricValues;
  primaryDimension: PrimaryFilterId;
  topNDimension?: SecondaryDimensionValues;
  secondaryDimension?: CompensationSecondaryDimension;
  chartType: CompensationChartType;
  exchangeRate?: number;
  entitiesOverride?: CompositionV2Entity[];
  children: DataProviderChildren<DataProviderSet | undefined>;
};

export const CompensationDataProvider = ({
  metric,
  primaryDimension,
  topNDimension,
  secondaryDimension,
  chartType,
  exchangeRate,
  entitiesOverride,
  children,
}: CompensationDataProviderProps) => {
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'week' });
  const { item } = useChart();
  const { pageState } = usePageState();
  const { revelioPageId } = useTabContentConfig();
  const geographyMode = useGeographyMode();

  const useTopGeosOnly =
    isOverviewPage(revelioPageId) &&
    chartType === 'box' &&
    secondaryDimension === 'geography';

  const payQuery = useTopGeosOnly ? PAY_V2_TOPGEOS_QUERY : PAY_V2_QUERY;

  if (topNDimension) {
    if (topNDimension === 'competitors') {
      return (
        <CompensationTopCompetitorsDataProvider
          primaryDimension={primaryDimension}
          metric={metric}
          chartType={chartType}
          exchangeRate={exchangeRate}
        >
          {children}
        </CompensationTopCompetitorsDataProvider>
      );
    }
  }

  if (
    (chartType === 'grouped-bar' || chartType === 'stacked-bar') &&
    (secondaryDimension === 'geography' || secondaryDimension === 'company')
  ) {
    return (
      <CompensationTopNDataProvider
        primaryDimension={primaryDimension}
        secondaryDimension={secondaryDimension}
        metric={metric}
        exchangeRate={exchangeRate}
      >
        {children}
      </CompensationTopNDataProvider>
    );
  }

  const baseCompensationFilters = getCompensationV2Filters({
    primaryDimension,
    effectiveFilters,
  });

  const compensationFiltersWithGeoOption: PayV2Req = {
    ...baseCompensationFilters,
    ...(geographyMode === 'administrative' && {
      options: { geo_breakdown_state_over_msa: true },
    }),
  };

  const overriddenDateFilters = compensationFiltersWithGeoOption;

  const selectedMultiEntities = pageState.multiPrimaryEntities?.[item?.id];
  const compensationFilters = getOverriddenMultiPrimaryCompensationVariables({
    baseVariables: overriddenDateFilters,
    chartType,
    selectedEntities: selectedMultiEntities,
  });

  const compensationFiltersWithEntitiesOverride = entitiesOverride
    ? { ...compensationFilters, entities: entitiesOverride }
    : compensationFilters;

  if (
    !compensationFiltersWithEntitiesOverride.filters.start_date ||
    !compensationFiltersWithEntitiesOverride.filters.end_date
  ) {
    return children({ data: undefined, status: DataProviderQueryStatus.Idle });
  }

  return (
    <CompensationQuery
      query={payQuery}
      variables={compensationFiltersWithEntitiesOverride}
    >
      {({ data, status }) =>
        children({
          data: data?.pay_v2
            ? convertPayV2Data({
                data,
                chartType,
                metric,
                secondaryDimension,
                exchangeRate,
                primaryDimension,
              })
            : undefined,
          status,
        })
      }
    </CompensationQuery>
  );
};

const CompensationQuery = ({
  query = PAY_V2_QUERY,
  variables,
  children,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // biome-ignore lint/suspicious/noExplicitAny: migrated from develop
  query?: TypedDocumentNode<any, any>;
  variables: PayV2Req;
  children: DataProviderChildren<GetPayV2Query | undefined>;
}) => {
  const isTaxonomyResolved = useIsTaxonomyResolved();
  const [{ data, status }] = useUrqlQueryWithStatus<GetPayV2Query>({
    query,
    variables: { req: variables },
    pause: !isTaxonomyResolved,
  });

  return children({ data, status });
};

// Helper to narrow Compensation query variables to the entities chosen in the Multi Primary Entity switcher.
const getOverriddenMultiPrimaryCompensationVariables = ({
  baseVariables,
  chartType,
  selectedEntities,
}: {
  baseVariables: PayV2Req;
  chartType: CompensationChartType;
  selectedEntities?: Array<{ id: string; levelId: string; name: string }>;
}): PayV2Req => {
  if (
    chartType !== 'box' ||
    !selectedEntities ||
    selectedEntities.length === 0
  ) {
    return baseVariables;
  }

  // Filter the entities based on the selected multi-primary entities
  const filteredEntities: PayV2Entity[] = selectedEntities
    .map((entity) => {
      const entityType =
        positionsEntityLookup[
          entity.levelId as keyof typeof positionsEntityLookup
        ];
      if (!entityType) return null;

      return {
        EntType: entityType,
        // biome-ignore lint/correctness/useParseIntRadix: migrated from develop
        EntID: parseInt(entity.id.split('_')?.slice(-1)?.[0] ?? '0'),
      };
    })
    .filter((entity): entity is PayV2Entity => entity !== null);

  // If no valid entities found, return the base variables
  if (filteredEntities.length === 0) {
    return baseVariables;
  }

  return {
    ...baseVariables,
    entities: filteredEntities,
  };
};
